import { useEffect, useMemo, useState } from "react";
import {
  Badge,
  Button,
  Card,
  Col,
  Form,
  Row,
  Spinner,
  Table,
} from "react-bootstrap";
import Swal from "sweetalert2";
import { toast } from "react-toastify";

import api from "../../services/api";
import ProductFormModal from "./ProductFormModal";

const TYPE_LABELS = {
  FOOD: "غذایی",
  PHYSICAL: "فیزیکی",
  SERVICE: "خدماتی",
  DIGITAL: "دیجیتال",
};

const STATUS_LABELS = {
  ACTIVE: "فعال",
  INACTIVE: "غیرفعال",
  ARCHIVED: "آرشیو شده",
};

const AdminProducts = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);

  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const [showForm, setShowForm] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);

  const fetchData = async () => {
    setLoading(true);

    try {
      const [
        productsResponse,
        categoriesResponse,
      ] = await Promise.all([
        api.get("/admin/products"),
        api.get("/product-categories"),
      ]);

      const productData = productsResponse.data;
      const categoryData = categoriesResponse.data;

      setProducts(
        Array.isArray(productData)
          ? productData
          : productData?.items || []
      );

      setCategories(
        Array.isArray(categoryData)
          ? categoryData
          : categoryData?.items || []
      );
    } catch (error) {
      toast.error(
        error.response?.data?.message ||
        "خطا در دریافت اطلاعات محصولات"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const filteredProducts = useMemo(() => {
    const query = search
      .trim()
      .toLowerCase();

    return products.filter((product) => {
      const matchesSearch =
        !query ||
        product.title
          ?.toLowerCase()
          .includes(query) ||
        product.slug
          ?.toLowerCase()
          .includes(query) ||
        product.code
          ?.toLowerCase()
          .includes(query);

      const matchesCategory =
        !categoryFilter ||
        product.category?.code === categoryFilter ||
        product.categoryCode === categoryFilter;

      const matchesStatus =
        !statusFilter ||
        product.status === statusFilter;

      return (
        matchesSearch &&
        matchesCategory &&
        matchesStatus
      );
    });
  }, [
    products,
    search,
    categoryFilter,
    statusFilter,
  ]);

  const openCreate = () => {
    setSelectedProduct(null);
    setShowForm(true);
  };

  const openEdit = async (product) => {
    try {
      const { data } = await api.get(
        `/admin/products/${product.code}`
      );

      setSelectedProduct(data);
      setShowForm(true);
    } catch (error) {
      toast.error(
        error.response?.data?.message ||
        "خطا در دریافت اطلاعات محصول"
      );
    }
  };

  const deleteProduct = async (product) => {
    const firstConfirm = await Swal.fire({
      title: "حذف محصول",
      text: `آیا از حذف «${product.title}» مطمئن هستید؟`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "بله، ادامه",
      cancelButtonText: "انصراف",
      reverseButtons: true,
    });

    if (!firstConfirm.isConfirmed) {
      return;
    }

    const secondConfirm = await Swal.fire({
      title: "تأیید نهایی",
      text: "این عملیات قابل بازگشت نیست. محصول حذف شود؟",
      icon: "error",
      showCancelButton: true,
      confirmButtonText: "بله، حذف کن",
      cancelButtonText: "انصراف",
      reverseButtons: true,
    });

    if (!secondConfirm.isConfirmed) {
      return;
    }

    try {
      await api.delete(
        `/admin/products/${product.code}`
      );

      toast.success(
        "محصول با موفقیت حذف شد."
      );

      await fetchData();
    } catch (error) {
      toast.error(
        error.response?.data?.message ||
        "خطا در حذف محصول"
      );
    }
  };

  const getImageUrl = (image) => {
    if (!image) {
      return "/images/product-1.jpg";
    }

    return `/${image.replace(/^\/+/, "")}`;
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" />
      </div>
    );
  }

  return (
    <>
      <Card className="border-0 shadow-sm">
        <Card.Body>
          <Row className="align-items-center g-3 mb-4">
            <Col lg={6}>
              <h4 className="mb-1">
                محصولات
              </h4>

              <p className="text-muted mb-0">
                مدیریت محصولات
              </p>
            </Col>

            <Col lg={6}>
              <div className="d-flex justify-content-lg-end">
                <Button
                  variant="primary"
                  onClick={openCreate}
                >
                  <i className="material-symbols-outlined align-middle me-1">
                    add
                  </i>

                  افزودن محصول
                </Button>
              </div>
            </Col>
          </Row>

          <Row className="g-3 mb-4">
            <Col lg={5}>
              <Form.Control
                value={search}
                onChange={(e) =>
                  setSearch(e.target.value)
                }
                placeholder="جستجو بر اساس عنوان، slug یا code..."
              />
            </Col>

            <Col lg={3}>
              <Form.Select
                value={categoryFilter}
                onChange={(e) =>
                  setCategoryFilter(e.target.value)
                }
              >
                <option value="">
                  همه دسته‌ها
                </option>

                {categories.map((category) => (
                  <option
                    key={category.code}
                    value={category.code}
                  >
                    {category.title}
                  </option>
                ))}
              </Form.Select>
            </Col>

            <Col lg={2}>
              <Form.Select
                value={statusFilter}
                onChange={(e) =>
                  setStatusFilter(e.target.value)
                }
              >
                <option value="">
                  همه وضعیت‌ها
                </option>

                <option value="ACTIVE">
                  فعال
                </option>

                <option value="INACTIVE">
                  غیرفعال
                </option>

                <option value="ARCHIVED">
                  آرشیو شده
                </option>
              </Form.Select>
            </Col>

            <Col lg={2}>
              <div className="h-100 d-flex align-items-center justify-content-lg-end">
                <Badge
                  bg="light"
                  text="dark"
                  className="px-3 py-2"
                >
                  {filteredProducts.length} محصول
                </Badge>
              </div>
            </Col>
          </Row>

          <div className="table-responsive">
            <Table
              hover
              className="align-middle mb-0"
            >
              <thead>
                <tr>
                  <th>#</th>
                  <th>تصویر</th>
                  <th>محصول</th>
                  <th>دسته</th>
                  <th>نوع</th>
                  <th>واحد</th>
                  <th>وضعیت</th>
                  <th>ترتیب</th>
                  <th className="text-center">
                    عملیات
                  </th>
                </tr>
              </thead>

              <tbody>
                {filteredProducts.length === 0 ? (
                  <tr>
                    <td
                      colSpan={9}
                      className="text-center text-muted py-5"
                    >
                      محصولی پیدا نشد.
                    </td>
                  </tr>
                ) : (
                  filteredProducts.map(
                    (product, index) => (
                      <tr
                        key={
                          product.code ||
                          product.id
                        }
                      >
                        <td>
                          {index + 1}
                        </td>

                        <td>
                          <div
                            className="rounded bg-light d-flex align-items-center justify-content-center"
                            style={{
                              width: 60,
                              height: 60,
                              overflow: "hidden",
                            }}
                          >
                            <img
                              src={getImageUrl(
                                product.image
                              )}
                              alt={
                                product.title
                              }
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                              }}
                              onError={(event) => {
                                event.currentTarget.src =
                                  "/images/product-1.jpg";
                              }}
                            />
                          </div>
                        </td>

                        <td>
                          <div className="fw-semibold">
                            {product.title}
                          </div>

                          <div
                            className="small text-muted"
                            dir="ltr"
                          >
                            {product.slug}
                          </div>

                          <div
                            className="small text-muted"
                            dir="ltr"
                          >
                            {product.code}
                          </div>
                        </td>

                        <td>
                          {product.category
                            ?.title ||
                            categories.find(
                              (item) =>
                                item.code ===
                                product.categoryCode
                            )?.title ||
                            "-"}
                        </td>

                        <td>
                          {TYPE_LABELS[
                            product.type
                          ] ||
                            product.type ||
                            "-"}
                        </td>

                        <td>
                          {product.unit ||
                            "-"}
                        </td>

                        <td>
                          <Badge
                            bg={
                              product.status ===
                              "ACTIVE"
                                ? "success"
                                : product.status ===
                                  "INACTIVE"
                                ? "warning"
                                : "secondary"
                            }
                          >
                            {STATUS_LABELS[
                              product.status
                            ] ||
                              product.status ||
                              "-"}
                          </Badge>
                        </td>

                        <td>
                          {product.sortOrder ??
                            0}
                        </td>

                        <td>
                          <div className="d-flex justify-content-center gap-2">
                            <Button
                              size="sm"
                              variant="warning"
                              onClick={() =>
                                openEdit(
                                  product
                                )
                              }
                            >
                              ویرایش
                            </Button>

                            <Button
                              size="sm"
                              variant="danger"
                              onClick={() =>
                                deleteProduct(
                                  product
                                )
                              }
                            >
                              حذف
                            </Button>
                          </div>
                        </td>
                      </tr>
                    )
                  )
                )}
              </tbody>
            </Table>
          </div>
        </Card.Body>
      </Card>

      <ProductFormModal
        show={showForm}
        onHide={() =>
          setShowForm(false)
        }
        product={selectedProduct}
        categories={categories}
        onSuccess={fetchData}
      />
    </>
  );
};

export default AdminProducts;